// Package imports:
import React, { useMemo } from 'react';
import { faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// Component imports:
import Alert from '../../../ui-elements/Alert/Alert';
import HeadSection from '../../../ui-elements/HeadSection/HeadSection';
import Link from '../../../ui-elements/Link/Link';
import Loading from '../../../ui-elements/Loading/Loading';
import Table from '../../../ui-elements/Table/Table';
import VerticalTable from '../../../ui-elements/Table/VerticalTable';
import DisplayBox from '../../DisplayBox/DisplayBox';
import PropertyNavBar from '../PropertyNavBar/PropertyNavBar';
// Service imports:
import { convertToPercentage, formatNumber } from '../../../services/utils';
// Type imports:
import { IEventReport } from '../../../types/KeldanTypes';
import { IPropertySearchResultOld } from '../../../types/PropertyTypes';

interface IProps {
    id: string,
    report: IEventReport<IPropertySearchResultOld>
}

const ViewReportOld: React.FC<IProps> = ({
    id,
    report
}) => {
    const propertyNumber = useMemo(() => {
        if (report instanceof Error) return undefined;
        if (!report?.overview.landonly) {
            return report?.overview?.property?.property?.property?.property_number
        } else {
            return undefined;
        }
    }, [report]);

    const nameNumber = useMemo(() => {
        if (report instanceof Error) return undefined;
        if (!report?.overview.landonly) {
            return report?.overview.property?.property?.property?.name_number;
        } else {
            return undefined;
        }
    }, [report]);

    const padDateString = (date?: string) => {
		if (!date) return '-';
        date = date.replaceAll('/', '.')
		if (date[2] !== '.') date = date.padStart(date.length + 1, '0')
		if (date[5] !== '.') date = date.substring(0,3) + '0' + date.substring(3)
		return date;
	}

    const displayAddress = (address: string | undefined, city: string | undefined) => {
        if (address === undefined || city === undefined) return '';
        return `${address}, ${city}`;
    }

    const info = useMemo(() => {
        if (report instanceof Error) return null;
        return report?.overview.property?.property?.property;
    }, [report]);

    return (
        <div className='main KCL_property-page'>
            <div className='main__inner fluid'>
                {report instanceof Error
                    ? <Alert type='error' headText={report.message} />
                    : report === undefined || report === null
                    ? <Loading />
                    : !report.overview.landonly
                    ? <>
                        <PropertyNavBar propertyNumber={propertyNumber} />
                        <div className='shell'>
                            <HeadSection
                                title={
                                    <div>
                                        <h1>{displayAddress(info?.name, info?.city)}</h1>
                                        <div className='propertyInfo'>
                                            <div className='propertyInfo_col'>
                                                <span className='propertyInfo_title'>
                                                    Fastanúmer
                                                </span>
                                                <span className='propertyInfo_value'>
                                                    {propertyNumber}
                                                </span>
                                            </div>
                                            <div className='propertyInfo_col'>
                                                <span className='propertyInfo_title'>
                                                    Merking
                                                </span>
                                                <span className='propertyInfo_value'>
                                                    {info?.units ? info?.units[0].tag : '-'}
                                                </span>
                                            </div>
                                            <div className='propertyInfo_col'>
                                                <span className='propertyInfo_title'>
                                                    Heitisnúmer
                                                </span>
                                                <span className='propertyInfo_value'>
                                                    {nameNumber}
                                                </span>
                                            </div>
                                            <div className='propertyInfo_col'>
                                                <span className='propertyInfo_title'>
                                                    Skýring
                                                </span>
                                                <span className='propertyInfo_value'>
                                                    {info?.units ? info?.units[0].description : '-'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                }
                            />
                            <div className='property-page-body'>
                                <DisplayBox
                                    title='Grunnupplýsingar'
                                    asideComponent={
                                        <Link
                                            targetBlank
                                            url={`/Pdf/Fasteignaskra-Gamalt/${id}`}
                                        >
                                            <FontAwesomeIcon icon={faFilePdf} /> Sækja skýrslu
                                        </Link>
                                    }
                                    className='property-report-box'
                                >
                                    <div className='italic-text'>
                                        Skýrsla keypt: {report.eventDate}
                                    </div>
                                    <div className='report-section'>
                                        <div className='vertical-table-wrapper'>
                                            <VerticalTable
                                                fallbackSymbol
                                                data={[report.overview.property?.property?.property]}
                                                columns={[{
                                                    title: 'Notkun',
                                                    dataAccessor: (property) => property?.usage
                                                }]}
                                                tableSize='lg'
                                            />
                                            <VerticalTable
                                                fallbackSymbol
                                                data={[report.overview.property?.property?.property]}
                                                columns={[{
                                                    title: 'Tryggingafélag',
                                                    dataAccessor: (property) => property?.insurance
                                                }]}
                                                tableSize='lg'
                                            />
                                        </div>
                                    </div>

                                    {report.overview.property?.property?.property?.owners &&
                                        <div className='report-section'>
                                            <h4>Eigendur</h4>
                                            <Table
                                                data={report.overview.property.property.property.owners}
                                                columns={[{
                                                    title: 'Nafn',
                                                    dataAccessor: ({name}) => name,
                                                    textAlign: 'left'
                                                }, {
                                                    title: 'Kennitala',
                                                    dataAccessor: ({ssn}) => ssn,
                                                    textAlign: 'left'
                                                }, {
                                                    title: 'Hlutfall',
                                                    dataAccessor: ({owned_part}) => convertToPercentage(owned_part == null ? null : owned_part/100, true, 1),
                                                    textAlign: 'right'
                                                }, {
                                                    title: 'Kaup',
                                                    dataAccessor: ({buy_date}) => padDateString(buy_date),
                                                    textAlign: 'right'
                                                }, {
                                                    title: 'Afhending',
                                                    dataAccessor: ({receive_date}) => padDateString(receive_date),
                                                    textAlign: 'right'
                                                }]}
                                                tableSize='lg'
                                            />
                                        </div>
                                    }

                                    {report.overview.property?.property?.property?.units &&
                                        <>
                                            {report.overview.property.property.property.units.map((u, index) => (
                                                <div className='report-section' key={`unit_${index}`}>
                                                    <h4>{u.usage}</h4>
                                                    <div className='vertical-table-wrapper'>
                                                        <VerticalTable
                                                            data={[u]}
                                                            columns={[{
                                                                title: 'Fastanúmer',
                                                                dataAccessor: ({property_number}) => property_number
                                                            }, {
                                                                title: 'Landnúmer',
                                                                dataAccessor: ({land_number}) => land_number
                                                            }, {
                                                                title: 'Heitisnúmer',
                                                                dataAccessor: ({name_number}) => name_number
                                                            }, {
                                                                title: 'Merking',
                                                                dataAccessor: ({tag}) => tag
                                                            }, {
                                                                title: 'Flatarmál',
                                                                dataAccessor: ({area, area_unit}) => `${area} ${area_unit}`
                                                            }, {
                                                                title: 'Byggingarár',
                                                                dataAccessor: ({building_year}) => building_year
                                                            }, {
                                                                title: 'Herbergi',
                                                                dataAccessor: ({rooms}) => rooms
                                                            }, {
                                                                title: 'Byggingarefni',
                                                                dataAccessor: ({building_material}) => building_material
                                                            }]}
                                                            tableSize='lg'
                                                        />
                                                        <VerticalTable
                                                            data={[u]}
                                                            columns={[{
                                                                title: 'Byggingarstig',
                                                                dataAccessor: ({building_status}) => building_status
                                                            }, {
                                                                title: 'Húsmat',
                                                                dataAccessor: ({house_estimation}) => `${formatNumber(house_estimation, '-', 0)} kr.`
                                                            }, {
                                                                title: 'Lóðarhlutamat',
                                                                dataAccessor: ({land_estimation}) => `${formatNumber(land_estimation, '-', 0)} kr.`
                                                            }, {
                                                                title: 'Fasteignamat',
                                                                dataAccessor: ({property_estimation}) => `${formatNumber(property_estimation, '-', 0)} kr.`
                                                            }, {
                                                                title: 'Endurstofnverð',
                                                                dataAccessor: ({reform_price}) => `${formatNumber(reform_price, '-', 0)} kr.`
                                                            }, {
                                                                title: 'Brunabótamat',
                                                                dataAccessor: ({burn_estimation}) => `${formatNumber(burn_estimation, '-', 0)} kr.`
                                                            }, {
                                                                title: 'Byggingarvísit.d.',
                                                                dataAccessor: ({building_index_date}) => padDateString(building_index_date)
                                                            }]}
                                                            tableSize='lg'
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </>
                                    }

                                </DisplayBox>

                                {report.overview.bondDetails && 
                                    <>
                                        <h2 className='bonds-heading'>Veðbandayfirlit</h2>
                                        <DisplayBox
                                            className='property-report-box'
                                        >
                                            {report.overview.bondDetails.bondDetails?.bondDetails?.owners?.map((owner, index) => (
                                                <div className='report-section' key={`bondowner_${index}`}>
                                                    <h4>{owner.name}</h4>
                                                    <div className='vertical-table-wrapper'>
                                                        <VerticalTable
                                                            data={[owner]}
                                                            columns={[{
                                                                title: 'Kennitala',
                                                                dataAccessor: ({ssn}) => ssn
                                                            }, {
                                                                title: 'Málsaðili',
                                                                dataAccessor: ({name}) => name
                                                            }, {
                                                                title: 'Eignarhlutfall',
                                                                dataAccessor: ({owned_part}) => convertToPercentage(owned_part === undefined ? null : owned_part/100, true, 1)
                                                            }, {
                                                                title: 'Tegund skjals',
                                                                dataAccessor: ({document_type}) => document_type
                                                            }, {
                                                                title: 'Númer',
                                                                dataAccessor: ({document_number}) => document_number
                                                            }]}
                                                            tableSize='lg'
                                                        />
                                                        <VerticalTable
                                                            data={[owner]}
                                                            columns={[{
                                                                title: 'Staða skjals',
                                                                dataAccessor: ({document_status}) => document_status
                                                            }, {
                                                                title: 'Útgáfudagur',
                                                                dataAccessor: ({document_published}) => padDateString(document_published)
                                                            }, {
                                                                title: 'Móttökudagur',
                                                                dataAccessor: ({document_received}) => padDateString(document_received)
                                                            }, {
                                                                title: 'Embættisnúmer',
                                                                dataAccessor: ({office_number}) => office_number
                                                            }]}
                                                            tableSize='lg'
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                            {report.overview.bondDetails.bondDetails?.bondDetails?.bonds?.map((bond, index) => (
                                                <div className='report-section' key={`bond_${index}`}>
                                                    <h4>{bond.number}. veðréttur</h4>
                                                    <div className='vertical-table-wrapper'>
                                                        <VerticalTable
                                                            data={[bond]}
                                                            columns={[{
                                                                title: 'Upphæð',
                                                                dataAccessor: ({amount, currency}) => `${amount} ${currency}`
                                                            }, {
                                                                title: 'Myntgengi',
                                                                dataAccessor: ({currency_rate}) => currency_rate
                                                            }, {
                                                                title: 'Númer',
                                                                dataAccessor: ({id}) => id
                                                            }, {
                                                                title: 'Tegund',
                                                                dataAccessor: ({type}) => type
                                                            }, {
                                                                title: 'Verðbinding',
                                                                dataAccessor: ({index_status}) => index_status
                                                            }]}
                                                            tableSize='lg'
                                                        />
                                                        <VerticalTable
                                                            data={[bond]}
                                                            columns={[{
                                                                title: 'Vísitölustig',
                                                                dataAccessor: ({index_state}) => index_state
                                                            }, {
                                                                title: 'Veðhafi',
                                                                dataAccessor: ({loaner}) => loaner
                                                            }, {
                                                                title: 'Útgáfudagur',
                                                                dataAccessor: ({published}) => padDateString(published)
                                                            }, {
                                                                title: 'Móttökudagur',
                                                                dataAccessor: ({received}) => padDateString(received)
                                                            }]}
                                                            tableSize='lg'
                                                        />
                                                    </div>
                                                </div>  
                                            ))}
                                            {report.overview.bondDetails.bondDetails?.bondDetails?.primary_allowances &&
                                                <div className='report-section'>
                                                    <h4>Frumheimildir</h4>
                                                    <Table
                                                        data={report.overview.bondDetails.bondDetails.bondDetails.primary_allowances}
                                                        columns={[{
                                                            title: 'Númer',
                                                            dataAccessor: ({document_number}) => document_number,
                                                            textAlign: 'left'
                                                        }, {
                                                            title: 'Staða',
                                                            dataAccessor: ({document_status}) => document_status,
                                                            textAlign: 'left'
                                                        }, {
                                                            title: 'Tegund',
                                                            dataAccessor: ({document_type}) => document_type,
                                                            textAlign: 'left'
                                                        }, {
                                                            title: 'Útgáfudagur',
                                                            dataAccessor: ({document_published}) => padDateString(document_published),
                                                            textAlign: 'right'
                                                        }, {
                                                            title: 'Móttökudagur',
                                                            dataAccessor: ({document_received}) => padDateString(document_received),
                                                            textAlign: 'right'
                                                        }]}
                                                        tableSize='lg'
                                                    />
                                                </div>
                                            }
                                            {report.overview.bondDetails.bondDetails?.bondDetails?.contracts &&
                                                <div className='report-section'>
                                                    <h4>Efnisatriði</h4>
                                                    <Table
                                                        data={report.overview.bondDetails.bondDetails.bondDetails.contracts}
                                                        columns={[{
                                                            title: 'Númer',
                                                            dataAccessor: ({document_number}) => document_number,
                                                            textAlign: 'left'
                                                        }, {
                                                            title: 'Staða',
                                                            dataAccessor: ({document_status}) => document_status,
                                                            textAlign: 'left'
                                                        }, {
                                                            title: 'Embættisnúmer',
                                                            dataAccessor: ({office_number}) => office_number,
                                                            textAlign: 'left'
                                                        }, {
                                                            title: 'Tegund',
                                                            dataAccessor: ({document_type}) => document_type,
                                                            textAlign: 'left'
                                                        }, {
                                                            title: 'Lýsing',
                                                            dataAccessor: ({description}) => description,
                                                            textAlign: 'left'
                                                        }]}
                                                        tableSize='lg'
                                                    />
                                                </div>
                                            }
                                        </DisplayBox>
                                    </>
                                }
                            </div>
                            <Alert
                                type='info'
                                headText='Athugið' 
                                text={
                                    <>
                                        <p>Birtar upplýsingar eru sóttar með tengingu við Fasteignaskrá Íslands. Allar upplýsingar eru veittar samkvæmt starfsreglum Fasteignaskrár og í samræmi við lög og reglur um persónuvernd og meðferð persónuupplýsinga.</p>
                                        <p>Reynt er að tryggja að upplýsingar séu réttar en Keldan getur ekki ábyrgst áreiðanleika þeirra.</p>
                                    </>
                                }
                            />
                        </div>
                    </>
                    : <div className='shell'>
                        <HeadSection
                            title={
                                <div>
                                    <h1>{report.overview.property?.property?.land?.name}, {report.overview.property?.property?.land?.city}</h1>
                                    <div className='propertyInfo'>
                                        <div className='propertyInfo_col'>
                                            <span className='propertyInfo_title'>
                                                Lóðarnúmer
                                            </span>
                                            <span className='propertyInfo_value'>
                                                {report.overview.property?.property?.land?.plot_number}
                                            </span>
                                        </div>
                                        <div className='propertyInfo_col'>
                                            <span className='propertyInfo_title'>
                                                Heitisnúmer
                                            </span>
                                            <span className='propertyInfo_value'>
                                                {report.overview.property?.property?.land?.name_number}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            }
                        />
                        <div className='property-page-body'>
                            <DisplayBox
                                title='Grunnupplýsingar'
                                asideComponent={
                                    <Link
                                        targetBlank
                                        url={`/Pdf/Fasteignaskra-Land-Gamalt/${id}`}
                                    >
                                        <FontAwesomeIcon icon={faFilePdf} /> Sækja skýrslu
                                    </Link>
                                }
                                className='property-report-box'
                            >
                                <div className='italic-text'>
                                    Skýrsla keypt: {report.eventDate}
                                </div>
                                <div className='report-section'>
                                    <div className='vertical-table-wrapper'>
                                        <VerticalTable
                                            data={[report.overview.property?.property?.land]}
                                            columns={[{
                                                title: 'Landnúmer',
                                                dataAccessor: (land) => land?.land_number
                                            }, {
                                                title: 'Heitisnúmer',
                                                dataAccessor: (land) => land?.name_number
                                            }, {
                                                title: 'Byggð',
                                                dataAccessor: (land) => land?.city
                                            }, {
                                                title: 'Lóðarnúmer',
                                                dataAccessor: (land) => land?.plot_number
                                            }]}
                                            tableSize='lg'
                                        />
                                        <VerticalTable
                                            data={[report.overview.property?.property?.land]}
                                            columns={[{
                                                title: 'Flatarmál',
                                                dataAccessor: (land) => `${land?.area} ${land?.area_unit}`
                                            }, {
                                                title: 'Lóðarmat',
                                                dataAccessor: (land) => `${formatNumber(land?.land_estimation, '-', 0)} kr.`
                                            }, {
                                                title: 'Notkun',
                                                dataAccessor: (land) => land?.usage
                                            }]}
                                            tableSize='lg'
                                        />
                                    </div>
                                </div>
                                {report.overview.property?.property?.land?.owners &&
                                    <div className='report-section'>
                                        <h4>Eigendur</h4>
                                        <Table
                                            data={report.overview.property.property.land.owners}
                                            columns={[{
                                                title: 'Nafn',
                                                dataAccessor: ({name}) => name,
                                                textAlign: 'left'
                                            }, {
                                                title: 'Kennitala',
                                                dataAccessor: ({ssn}) => ssn,
                                                textAlign: 'left'
                                            }, {
                                                title: 'Hlutfall',
                                                dataAccessor: ({owned_part}) => convertToPercentage(owned_part === undefined || owned_part === null ? null : owned_part/100, true, 1),
                                                textAlign: 'right'
                                            }, {
                                                title: 'Kaup',
                                                dataAccessor: ({buy_date}) => padDateString(buy_date),
                                                textAlign: 'right'
                                            }, {
                                                title: 'Afhending',
                                                dataAccessor: ({receive_date}) => padDateString(receive_date),
                                                textAlign: 'right'
                                            }]}
                                            tableSize='lg'
                                        />
                                    </div>
                                }
                            </DisplayBox>

                            {report.overview.bondDetails && 
                                    <>
                                        <h2 className='bonds-heading'>Veðbandayfirlit</h2>
                                        <DisplayBox
                                            className='property-report-box'
                                        >
                                            {report.overview.bondDetails.bondDetails?.bondDetails?.owners?.map((owner, index) => (
                                                <div className='report-section' key={`bondowner_${index}`}>
                                                    <h4>{owner.name}</h4>
                                                    <div className='vertical-table-wrapper'>
                                                        <VerticalTable
                                                            data={[owner]}
                                                            columns={[{
                                                                title: 'Kennitala',
                                                                dataAccessor: ({ssn}) => ssn
                                                            }, {
                                                                title: 'Málsaðili',
                                                                dataAccessor: ({name}) => name
                                                            }, {
                                                                title: 'Eignarhlutfall',
                                                                dataAccessor: ({owned_part}) => convertToPercentage(owned_part === undefined ? null : owned_part/100, true, 1)
                                                            }, {
                                                                title: 'Tegund skjals',
                                                                dataAccessor: ({document_type}) => document_type
                                                            }, {
                                                                title: 'Númer',
                                                                dataAccessor: ({document_number}) => document_number
                                                            }]}
                                                            tableSize='lg'
                                                        />
                                                        <VerticalTable
                                                            data={[owner]}
                                                            columns={[{
                                                                title: 'Staða skjals',
                                                                dataAccessor: ({document_status}) => document_status
                                                            }, {
                                                                title: 'Útgáfudagur',
                                                                dataAccessor: ({document_published}) => padDateString(document_published)
                                                            }, {
                                                                title: 'Móttökudagur',
                                                                dataAccessor: ({document_received}) => padDateString(document_received)
                                                            }, {
                                                                title: 'Embættisnúmer',
                                                                dataAccessor: ({office_number}) => office_number
                                                            }]}
                                                            tableSize='lg'
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                            {report.overview.bondDetails.bondDetails?.bondDetails?.bonds?.map((bond, index) => (
                                                <div className='report-section' key={`bond_${index}`}>
                                                    <h4>{bond.number}. veðréttur</h4>
                                                    <div className='vertical-table-wrapper'>
                                                        <VerticalTable
                                                            data={[bond]}
                                                            columns={[{
                                                                title: 'Upphæð',
                                                                dataAccessor: ({amount, currency}) => `${amount} ${currency}`
                                                            }, {
                                                                title: 'Myntgengi',
                                                                dataAccessor: ({currency_rate}) => currency_rate
                                                            }, {
                                                                title: 'Númer',
                                                                dataAccessor: ({id}) => id
                                                            }, {
                                                                title: 'Tegund',
                                                                dataAccessor: ({type}) => type
                                                            }, {
                                                                title: 'Verðbinding',
                                                                dataAccessor: ({index_status}) => index_status
                                                            }]}
                                                            tableSize='lg'
                                                        />
                                                        <VerticalTable
                                                            data={[bond]}
                                                            columns={[{
                                                                title: 'Vísitölustig',
                                                                dataAccessor: ({index_state}) => index_state
                                                            }, {
                                                                title: 'Veðhafi',
                                                                dataAccessor: ({loaner}) => loaner
                                                            }, {
                                                                title: 'Útgáfudagur',
                                                                dataAccessor: ({published}) => padDateString(published)
                                                            }, {
                                                                title: 'Móttökudagur',
                                                                dataAccessor: ({received}) => padDateString(received)
                                                            }]}
                                                            tableSize='lg'
                                                        />
                                                    </div>
                                                </div>  
                                            ))}
                                            {report.overview.bondDetails.bondDetails?.bondDetails?.primary_allowances &&
                                                <div className='report-section'>
                                                    <h4>Frumheimildir</h4>
                                                    <Table
                                                        data={report.overview.bondDetails.bondDetails.bondDetails.primary_allowances}
                                                        columns={[{
                                                            title: 'Númer',
                                                            dataAccessor: ({document_number}) => document_number,
                                                            textAlign: 'left'
                                                        }, {
                                                            title: 'Staða',
                                                            dataAccessor: ({document_status}) => document_status,
                                                            textAlign: 'left'
                                                        }, {
                                                            title: 'Tegund',
                                                            dataAccessor: ({document_type}) => document_type,
                                                            textAlign: 'left'
                                                        }, {
                                                            title: 'Útgáfudagur',
                                                            dataAccessor: ({document_published}) => padDateString(document_published),
                                                            textAlign: 'right'
                                                        }, {
                                                            title: 'Móttökudagur',
                                                            dataAccessor: ({document_received}) => padDateString(document_received),
                                                            textAlign: 'right'
                                                        }]}
                                                        tableSize='lg'
                                                    />
                                                </div>
                                            }
                                            {report.overview.bondDetails.bondDetails?.bondDetails?.contracts &&
                                                <div className='report-section'>
                                                    <h4>Efnisatriði</h4>
                                                    <Table
                                                        data={report.overview.bondDetails.bondDetails.bondDetails.contracts}
                                                        columns={[{
                                                            title: 'Númer',
                                                            dataAccessor: ({document_number}) => document_number,
                                                            textAlign: 'left'
                                                        }, {
                                                            title: 'Staða',
                                                            dataAccessor: ({document_status}) => document_status,
                                                            textAlign: 'left'
                                                        }, {
                                                            title: 'Embættisnúmer',
                                                            dataAccessor: ({office_number}) => office_number,
                                                            textAlign: 'left'
                                                        }, {
                                                            title: 'Tegund',
                                                            dataAccessor: ({document_type}) => document_type,
                                                            textAlign: 'left'
                                                        }, {
                                                            title: 'Lýsing',
                                                            dataAccessor: ({description}) => description,
                                                            textAlign: 'left'
                                                        }]}
                                                        tableSize='lg'
                                                    />
                                                </div>
                                            }
                                        </DisplayBox>
                                    </>
                                }
                        </div>
                        <Alert
                            withCloseIcon
                            type='info'
                            headText='Athugið' 
                            text={
                                <>
                                    <p>Birtar upplýsingar eru sóttar með tengingu við Fasteignaskrá Íslands. Allar upplýsingar eru veittar samkvæmt starfsreglum Fasteignaskrár og í samræmi við lög og reglur um persónuvernd og meðferð persónuupplýsinga.</p>
                                    <p>Reynt er að tryggja að upplýsingar séu réttar en Keldan getur ekki ábyrgst áreiðanleika þeirra.</p>
                                </>
                            }
                        />
                    </div>
                }
            </div>
        </div>
    );
}

export default ViewReportOld;